import React from 'react';
import { Helmet } from 'react-helmet-async';
import PageHeader from '../components/PageHeader';

const LAST_UPDATED = 'June 1, 2025';

function PrivacyPolicy() {
	return (
		<>
			<Helmet>
				<title>Privacy Policy | A-Z Home Improvement LLC</title>
				<meta name="description" content="Privacy policy for A-Z Home Improvement LLC. Learn how we collect, use, and protect your personal information." />
			</Helmet>
			<PageHeader title="Privacy Policy" subtitle={`Last updated: ${LAST_UPDATED}`} />

			<section className="section">
				<div className="container" style={{ maxWidth: 760 }}>
					<p>A-Z Home Improvement LLC ("A-Z," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or contact us about our services.</p>

					<h2>1. Information We Collect</h2>
					<h3>Information You Provide</h3>
					<ul>
						<li><strong>Contact details:</strong> Name, email address, phone number, and mailing address when you submit our contact form or request an estimate.</li>
						<li><strong>Project details:</strong> Information about your home improvement project, including the type of work, location, and any notes you provide.</li>
						<li><strong>Communications:</strong> Records of email, phone, and SMS communications with our team.</li>
						<li><strong>Consent records:</strong> Date, time, and method of your SMS opt-in consent.</li>
					</ul>
					<h3>Information Collected Automatically</h3>
					<ul>
						<li><strong>Log data:</strong> IP address, browser type, referring URL, pages visited, and time spent on pages.</li>
						<li><strong>Cookies:</strong> Small data files stored on your device to remember preferences. You can control cookies via your browser settings.</li>
					</ul>

					<h2>2. How We Use Your Information</h2>
					<p>We use the information we collect to:</p>
					<ul>
						<li>Respond to your inquiry and schedule your free estimate appointment.</li>
						<li>Communicate about your project — via phone, email, or SMS (with your consent).</li>
						<li>Send appointment reminders, project updates, and status notifications.</li>
						<li>Send promotional messages about our services (SMS only with explicit opt-in).</li>
						<li>Maintain records for warranty, permitting, and compliance purposes.</li>
						<li>Improve our website, services, and customer experience.</li>
						<li>Comply with legal obligations.</li>
					</ul>

					<h2>3. SMS Communications</h2>
					<p>If you opt in to SMS communications, we use your phone number to send estimate confirmations, appointment reminders, project updates, and promotional messages. <strong>No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.</strong> You may opt out at any time by replying STOP. See our <a href="/sms-policy" style={{ color: 'var(--brand)' }}>SMS Policy</a> for full details.</p>

					<h2>4. Information Sharing and Disclosure</h2>
					<p>We do not sell your personal information. We may share information only in the following limited circumstances:</p>
					<ul>
						<li><strong>Service providers:</strong> Third-party vendors who help us operate our business (email services, scheduling software, SMS platforms, CRM), under confidentiality agreements.</li>
						<li><strong>Legal compliance:</strong> When required by law, subpoena, or government request.</li>
						<li><strong>Business transfer:</strong> In connection with a merger, acquisition, or sale of all or substantially all of our assets.</li>
					</ul>

					<h2>5. Data Retention</h2>
					<p>We retain your contact and project information for as long as necessary to fulfill the purposes described above — typically 7 years to comply with contractor record-keeping requirements. SMS opt-in and opt-out records are retained for a minimum of 4 years per TCPA guidance.</p>

					<h2>6. Security</h2>
					<p>We implement reasonable technical and organizational safeguards to protect your personal data from unauthorized access, disclosure, or destruction. This includes encrypted data transmission (HTTPS) and access controls on our internal systems. No method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>

					<h2>7. Your Rights (California Residents)</h2>
					<p>If you are a California resident, the California Consumer Privacy Act (CCPA) grants you the right to request disclosure of personal data we've collected about you, request deletion of your personal data, and opt out of the sale of personal data. We do not sell personal data. To exercise these rights, contact us at the information below.</p>

					<h2>8. Children's Privacy</h2>
					<p>Our website and services are not directed at children under 13. We do not knowingly collect personal information from children. If you believe a child has submitted information to us, please contact us immediately.</p>

					<h2>9. Changes to This Policy</h2>
					<p>We may update this Privacy Policy from time to time. The "last updated" date at the top of this page reflects changes. Continued use of our website after changes constitutes acceptance of the updated policy.</p>

					<h2>10. Contact Us</h2>
					<p>For privacy-related questions, requests, or concerns:</p>
					<address style={{ fontStyle: 'normal', background: '#f4f4f4', borderRadius: 8, padding: 20, marginTop: 4 }}>
						<strong>A-Z Home Improvement LLC</strong><br />
						3842 W. McDowell Rd, Suite 110<br />
						Phoenix, AZ 85009<br />
						Phone: <a href="tel:+16023902536" style={{ color: 'var(--brand)' }}>(602) 390-2536</a><br />
						Email: <a href="mailto:support@azhomefundingservices.com" style={{ color: 'var(--brand)' }}>support@azhomefundingservices.com</a>
					</address>
				</div>
			</section>
		</>
	);
}

export default PrivacyPolicy;
